import { Step, Section, Field } from 'components/Wizard2/WizardClasses';
import * as WizardClasses from 'components/Wizard2/WizardClasses';
import getStaticFields from './staticFields'
import shortid from 'shortid';
import { format } from 'date-fns';

const initialStepId = shortid.generate();
const initialSectionId = shortid.generate();
const initialFieldId = shortid.generate();
const { SIGNER_EMAIL, SIGNER_NAME } = getStaticFields(WizardClasses)
const STATIC_FIELDS = {
  [SIGNER_EMAIL.fieldId]: SIGNER_EMAIL,
  [SIGNER_NAME.fieldId]: SIGNER_NAME
}

const getInitialSignatureValues = () => ({
  SIGNATURE: '',  
  SIGNED_DATE: process.env.THIRD_PARTY_SIGNATURE_DEFAULT_DATE
    ? process.env.STATE_TIMEZONE
      ? format(new Date(new Date().toLocaleString('en-US', {timeZone: process.env.STATE_TIMEZONE})), 'MM/dd/yyyy')
      : format(new Date(), 'MM/dd/yyyy')
    : '',
  attestations: {},
})

export const initialState = {
  wizard: {
    title: "New Wizard",
    description: "Description",
    formDefinitionId: 0,
    category: "",
    entityTypeId: 54,
    entitySubTypeId: 202,
    stepOrder: [initialStepId],
    sourceId: 0,
    docTitleSrc: '',
    docId: '',
    formTitle: '',
    internalReviewRequired: false,
    internalReviewRequiredSet: new Set(),
    noLoginRequired: true,
    onlyRenderChangedFieldsOnForm: false,
    draftsDisabled: false,
    feeDescription: '',
    steps: {
      [initialStepId]: new Step({stepId: initialStepId, sectionOrder: [initialSectionId]})
    },
    sections: {
      [initialSectionId]: new Section({sectionId: initialSectionId, stepId: initialStepId, fieldOrder: [initialFieldId]})
    },
    fields: {
      [initialFieldId]: new Field({fieldId: initialFieldId, sectionId: initialSectionId}),
      ...STATIC_FIELDS
    },
    amendmentNum: 0,
    meta: {},
    globalVariables: {
      boolean: {
        order: [],
        variables: {}
      },
      string: {
        order: [],
        variables: {}
      },
      number: {
        order: [],
        variables: {}
      },
      businessRule: {
        order: [],
        variables: {}
      }
    },
    wizardAttributesLogic: {nodes: {}, comments: {}},
  },
  loadingWizard: true,
  activeStep: 0,
  targetStep: 0,
  validating: false,
  repeaterAdding: null,
  repeaterEditing: false,
  helpShown: false,
  draftId: false,
  savingDraft: null,
  readOnly: false,
  mode: 'live',
  diff: {original: {}, current: {}, originalByFieldId: {}},
  preview: false,
  uploading: false,
  correctionMode: false,
  showDiff: false,
  infoMessages: [],
  requirePrintMailMethod: new Set(),
  submitted: false,
  submitting: false,
  canChangeEntryType: false,
  canChangeCorrectionEntryType: null,
  wizardLoadFailed: false,
  wizardLoadErrorMessage: null,
  wizardLoadErrorTitle: null,
  signature: null,
  signatureValues: getInitialSignatureValues(),
  signatureErrors: {SIGNATURE: null, SIGNED_DATE: null},
  genericSuccess: null,
  thirdPartySigningAllowed: false
}

const WizardReducer = (state = initialState, action) => {
  //deepFreeze(state)
  switch (action.type) {
    case "RESET_WIZARD_STATE":
      return {...initialState}
    case "POPULATE_WIZARD":
      return {
        ...state,
        loadingWizard: false,
        wizard: {
          ...state.wizard,
          title: action.title,
          formDefinitionId: action.formDefinitionId,
          stepOrder: action.stepOrder,
          steps: action.steps,
          sections: action.sections,
          fields: {...STATIC_FIELDS, ...action.fields},
          category: action.category,
          sourceId: action.sourceId,
          meta: action.meta ? action.meta : {},
          docTitleSrc: action.docTitleSrc,
          docId: action.docId,
          formTitle: action.formTitle,
          amendmentNum: action.amendmentNum,
          internalReviewRequired: action.internalReviewRequired,
          noLoginRequired: action.noLoginRequired,
          feeDescription: action.feeDescription,
          onlyRenderChangedFieldsOnForm: action.onlyRenderChangedFieldsOnForm,
          draftsDisabled: action.draftsDisabled || false,
          ...(action.globalVariables ? {globalVariables : action.globalVariables} : {}),
          ...(action.wizardAttributesLogic ? {wizardAttributesLogic : action.wizardAttributesLogic} : {})
        },
        helpShown: false,
        activeStep: action.activeStep ? action.activeStep : 0,
        draftId: action.draftId,
        readOnly: action.readOnly,
        mode: action.mode ? action.mode : "live",
        preview: action.preview,
        diff: {
          ...state.diff,
          original: action.original ? action.original : {},
          originalByFieldId: action.originalByFieldId ? action.originalByFieldId : {}
        },
        correctionMode: action.correctionMode ? true : false,
        showDiff: action.showDiff,
        requirePrintMailMethod: action.requirePrintMailMethod
          ? new Set(["GLOBAL"])
          : action.fieldsRequirePrintMailMethod
            ? new Set(action.fieldsRequirePrintMailMethod)
            : new Set(),
        canChangeEntryType: action.canChangeEntryType || false,
        canChangeCorrectionEntryType: action.canChangeCorrectionEntryType !== undefined ? action.canChangeCorrectionEntryType : null,
        signature: action.signature || null,
        thirdPartySigningAllowed: action.thirdPartySigningAllowed || false
      }
    case "SPREAD_WIZARD_ATTRIBUTES":
      return {
        ...state,
        wizard: {
          ...state.wizard,
          ...action.payload
        }
      }
    case "TOGGLE_WIZARD_LOADING":
    return {...state, loadingWizard: action.loading}
    case "SET_WIZARD_LOAD_FAILED":
      return {...state, wizardLoadFailed: action.wizardLoadFailed}
    case "SET_WIZARD_ERROR_MESSAGE":
      return {...state, wizardLoadErrorMessage: action.message, wizardLoadErrorTitle: action.title ? action.title : null}
    case "SET_SUBMITTED":
      return {...state, submitted: action.submitted}
    case "SET_SUBMITTING":
      return {...state, submitting: action.submitting}
    case "SET_ACTIVE_WIZARD_STEP":
      return {...state, activeStep: action.activeStep}
    case "SET_TARGET_STEP":
      return {...state, targetStep: action.targetStep}
    case "SET_FIELD_ATTRIBUTES":
      return {...state, wizard: {...state.wizard, fields: {
        ...state.wizard.fields,
        [action.fieldId]: {
          ...state.wizard.fields[action.fieldId],
          ...action.payload
        }
      }}}
    case "SET_STEP_ATTRIBUTES":
      return {...state, wizard: {
        ...state.wizard,
        steps: {
          ...state.wizard.steps,
          [action.stepId]: {
            ...state.wizard.steps[action.stepId],
            ...action.payload
          }
        }
      }}
    case "SET_WIZARD_ATTRIBUTES":
      return {...state, wizard: {...state.wizard, ...action.payload}}
    case "SET_WIZARD_SIGNATURE_VALUES":
      return {...state, signatureValues: action.signatureValues}
    case "RESET_WIZARD_SIGNATURE_VALUES":
      return {...state, signatureValues: getInitialSignatureValues()}
    case "SET_WIZARD_SIGNATURE_ERRORS":
      return {...state, signatureErrors: action.signatureErrors}
    case "SET_GENERIC_SUCCESS":
      return {...state, genericSuccess: action.message}
    case "TOGGLE_HELP_MODAL":
      return {...state, helpShown: action.helpShown}
    case "TOGGLE_VALIDATING":
      return {...state, validating: action.validating}
    case "TOGGLE_SAVING_DRAFT":
      return {...state, savingDraft: action.savingDraft}
    case "SET_DRAFT_ID":
      return {...state, draftId: action.draftId}
    case "SET_UPLOADING":
      return {...state, uploading: action.uploading}
    case "CLEAR_INFO_MESSAGES":
      return {...state, infoMessages: []}
    case "ADD_INFO_MESSAGE":
      return {...state, infoMessages: [...state.infoMessages, action.message]}
    case "ADD_REQUIRE_PRINT_MAIL_METHOD": {
      if(!state.requirePrintMailMethod.has(action.fieldId)){
        let requirePrintMailMethod = new Set(state.requirePrintMailMethod)
        requirePrintMailMethod.add(action.fieldId)
        return {...state, requirePrintMailMethod}
      }else{
        return state;
      }
    }
    case "REMOVE_REQUIRE_PRINT_MAIL_METHOD": {
      if(state.requirePrintMailMethod.has(action.fieldId)){
        let requirePrintMailMethod = new Set(state.requirePrintMailMethod)
        requirePrintMailMethod.delete(action.fieldId)
        return {...state, requirePrintMailMethod}
      }else{
        return state;
      }
    }
    case "ADD_INTERNAL_REVIEW_REQUIRED": {
      if(!state.wizard.internalReviewRequiredSet.has(action.fieldId)){
        let internalReviewRequiredSet = new Set(state.wizard.internalReviewRequiredSet)
        internalReviewRequiredSet.add(action.fieldId)
        return {
          ...state,
          wizard: {
            ...state.wizard,
            internalReviewRequiredSet
          }
        }
      }else{
        return state;
      }
    }
    case "REMOVE_INTERNAL_REVIEW_REQUIRED": {
      if(state.wizard.internalReviewRequiredSet.has(action.fieldId)){
        let internalReviewRequiredSet = new Set(state.wizard.internalReviewRequiredSet)
        internalReviewRequiredSet.delete(action.fieldId)
        return {
          ...state,
          wizard: {
            ...state.wizard,
            internalReviewRequiredSet
          }
        }
      }else{
        return state;
      }
    }
    case 'UPDATE_WIZARD_META':
      return {
        ...state,
        wizard: {
          ...state.wizard,
          meta: action.newMeta
        }
      }
    case 'SET_REPEATER_EDITING':
      return {
        ...state,
        repeaterEditing: action.repeaterEditing
      }
    default:
      return state;
  }
}

export default WizardReducer;



// WEBPACK FOOTER //
// ./src/components/Wizard2/WizardReducer.js